import type { PageInfo } from '../types';

/**
 * The tree of maps (P06, F-6): which map is nested into which. One walk for the Studio rail's
 * «Карты кампании», the crumbs above a map and the «Вложить в…» list — no third traversal.
 */

type TreePage = Pick<PageInfo, 'id' | 'parentPageId' | 'sortOrder'>;

/** The depth of a page in the hierarchy (a root is 0). A cycle in the data stops the walk. */
export function getDepth(
  page: Pick<PageInfo, 'id' | 'parentPageId'>,
  allPages: readonly Pick<PageInfo, 'id' | 'parentPageId'>[],
  visited = new Set<string>(),
): number {
  if (page.parentPageId === null || visited.has(page.id)) return 0;
  visited.add(page.id);
  const parent = allPages.find((p) => p.id === page.parentPageId);
  if (!parent) return 0;
  return 1 + getDepth(parent, allPages, visited);
}

/** Pages in tree order: a parent before its children (depth first), each level by sortOrder. */
export function buildTreeOrder<P extends TreePage>(pages: readonly P[]): P[] {
  const childrenOf = new Map<string | null, P[]>();
  for (const p of pages) {
    const parentId = p.parentPageId ?? null;
    if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
    childrenOf.get(parentId)!.push(p);
  }
  for (const children of childrenOf.values()) children.sort((a, b) => a.sortOrder - b.sortOrder);

  const result: P[] = [];
  const seen = new Set<string>();
  const visit = (parentId: string | null): void => {
    for (const child of childrenOf.get(parentId) ?? []) {
      if (seen.has(child.id)) continue;
      seen.add(child.id);
      result.push(child);
      visit(child.id);
    }
  };
  visit(null);
  // A page whose parent is gone (or in a cycle) is not lost.
  for (const p of pages) if (!seen.has(p.id)) result.push(p);
  return result;
}

/** From the root to the page itself: «Мир › Регион › Карта». Stops at a missing parent or a cycle. */
export function pagePath<P extends Pick<PageInfo, 'id' | 'parentPageId'>>(pageId: string, pages: readonly P[]): P[] {
  const path: P[] = [];
  const seen = new Set<string>();
  let current = pages.find((p) => p.id === pageId);
  while (current !== undefined && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    const parentId: string | null = current.parentPageId;
    current = parentId === null ? undefined : pages.find((p) => p.id === parentId);
  }
  return path;
}

/** Ids of every map nested (at any depth) into the page: they cannot be its parent. */
export function descendantIds(pageId: string, pages: readonly Pick<PageInfo, 'id' | 'parentPageId'>[]): Set<string> {
  const out = new Set<string>();
  const queue = [pageId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const p of pages) {
      if (p.parentPageId === id && !out.has(p.id) && p.id !== pageId) {
        out.add(p.id);
        queue.push(p.id);
      }
    }
  }
  return out;
}

/** One visible row of a map tree (sidebar polish П9): the page, its level, and whether it has nested maps / is open. */
export interface TreeRow<P> {
  readonly page: P;
  readonly depth: number;
  /** null — no nested maps. */
  readonly open: boolean | null;
  /** The tree has nested maps: every row keeps the expander column (an empty place on a leaf), so a child's name is always
   *  16 px right of its parent's (31.10: a per-level column put a leaf child on its parent's line). A flat list has none. */
  readonly column: boolean;
}

/**
 * The rows of a map tree as drawn: `roots` and, under an open map, its children (by sortOrder), indented a level each.
 * A search (`matches`) shows every map that matches together with its path — those levels are open whatever the GM chose.
 */
export function visibleTreeRows<P extends TreePage>(
  pages: readonly P[],
  roots: readonly P[],
  isOpen: (pageId: string) => boolean,
  matches?: (page: P) => boolean,
): TreeRow<P>[] {
  const childrenOf = new Map<string, P[]>();
  for (const p of pages) {
    if (p.parentPageId === null || p.parentPageId === undefined) continue;
    const list = childrenOf.get(p.parentPageId) ?? [];
    list.push(p);
    childrenOf.set(p.parentPageId, list);
  }
  for (const list of childrenOf.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);
  const hitMemo = new Map<string, boolean>();
  const hit = (p: P, seen: Set<string> = new Set()): boolean => {
    if (matches === undefined) return true;
    const known = hitMemo.get(p.id);
    if (known !== undefined) return known;
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    const value = matches(p) || (childrenOf.get(p.id) ?? []).some((c) => hit(c, seen));
    hitMemo.set(p.id, value);
    return value;
  };
  const out: TreeRow<P>[] = [];
  const seen = new Set<string>();
  const column = roots.some((p) => hit(p) && (childrenOf.get(p.id) ?? []).some((c) => hit(c)));
  const walk = (list: readonly P[], depth: number): void => {
    const shown = list.filter((p) => !seen.has(p.id) && hit(p));
    for (const p of shown) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      const kids = childrenOf.get(p.id) ?? [];
      const open = kids.length === 0 ? null : matches !== undefined ? true : isOpen(p.id);
      out.push({ page: p, depth, open, column });
      if (open === true) walk(kids, depth + 1);
    }
  };
  walk(roots, 0);
  return out;
}
